<template>
  <v-card class="campaign-impact-chart-card">
    <v-card-title class="d-flex justify-space-between align-center">
      <div class="d-flex align-center">
        <img :src="require('@/assets/icons/mar.ia-cor.svg')" alt="mar.ia" style="height: 24px" class="mr-2" />
        <h3 class="chart-title">Impacto da campanha na quantidade média de produtos vendidos</h3>
      </div>
      <div class="d-flex align-center">
        <export-data button-label="Exportar dados por produto" :data-fetcher="getDataToExport" filename="uplift_por_produto" ghost></export-data>
        <v-btn-toggle v-model="chartViewMode" mandatory class="chart-view-toggle">
          <v-btn small value="bar">
            <v-icon small>mdi-chart-bar</v-icon>
          </v-btn>
          <v-btn small value="line">
            <v-icon small>mdi-chart-line</v-icon>
          </v-btn>
        </v-btn-toggle>
      </div>
    </v-card-title>
    <v-card-text>
      <p class="chart-description mb-4">
        Mostra a variação da venda média por produto durante a campanha, comparando clientes que receberam a oferta com o grupo de controle. Na tela são
        exibidos apenas os 10 produtos com maior impacto. Para consultar todos os produtos da campanha, utilize a exportação.
      </p>
      <v-row>
        <v-col v-if="productOptions.length" cols="12" md="3">
          <v-select v-model="selectedProduct" :items="productOptions" label="" dense outlined hide-details />
        </v-col>
      </v-row>
      <div class="chart-container mt-4">
        <apexchart
          v-if="chartData.series.length > 0"
          :key="`${chartViewMode}-${currentPosition}`"
          :type="chartViewMode"
          height="200"
          :options="chartOptions"
          :series="chartData.series"
        />
        <div v-else class="text-center pa-8">
          <v-icon size="64" color="grey">mdi-chart-line</v-icon>
          <p class="mt-2 grey--text">Nenhum dado disponível para exibição</p>
        </div>
      </div>
      <div class="chart-legend mt-4 px-2">
        <div class="legend-section">
          <div class="legend-item">
            <div class="legend-color segment-clients"></div>
            <span>Clientes do segmento</span>
          </div>
          <div class="legend-item">
            <div class="legend-color offer-clients"></div>
            <span>Clientes que receberam a oferta</span>
          </div>
          <div class="legend-item">
            <div class="legend-color no-offer-clients"></div>
            <span>Clientes que não receberam a oferta</span>
          </div>
          <div class="legend-item">
            <div class="legend-period"></div>
            <span>Período vigente da campanha</span>
          </div>
        </div>
        <div v-if="chartData.series.length > 0" class="chart-navigator">
          <apex-chart-navigator
            :chart-labels-length="fullChartData.categories.length"
            :interval="14"
            :current-position="currentPosition"
            @update:currentPosition="updateCurrentPosition"
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { DateTime } from 'luxon'
import ExportData from '@/components/export-data/ExportData.vue'
import ApexChartNavigator from '@/components/ApexChartNavigator.vue'
import { getOfferTypeName } from '@/modules/campanhas/gestao-ofertas-v2/helpers/offerType.js'

export default {
  name: 'CampaignImpactChart',
  components: {
    apexchart: VueApexCharts,
    ExportData,
    ApexChartNavigator,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    products: {
      type: Array,
      default: () => [],
    },
    offerType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      chartViewMode: 'line',
      selectedProduct: null,
      currentPosition: 0,
      chartData: {
        categories: [],
        series: [],
      },
      fullChartData: {
        categories: [],
        series: [],
      },
    }
  },
  computed: {
    yAxisMax() {
      const extractMax = (seriesArr) => {
        if (!Array.isArray(seriesArr)) return 0
        let maxVal = 0
        seriesArr.forEach((s) => {
          const data = Array.isArray(s?.data) ? s.data : []
          data.forEach((v) => {
            if (typeof v === 'number' && isFinite(v)) {
              if (v > maxVal) maxVal = v
            }
          })
        })
        return maxVal
      }

      let maxVal = extractMax(this.chartData?.series)
      if (maxVal === 0) {
        maxVal = extractMax(this.fullChartData?.series)
      }

      const ceil = Math.ceil(maxVal)
      return ceil < 1 ? 1 : ceil
    },
    xAxisTickAmount() {
      if (!this.chartData?.series || this.chartData.series.length === 0) return undefined
      const firstSeries = this.chartData.series[0]
      if (!firstSeries?.data) return undefined
      return firstSeries.data.length
    },
    xAxisCategories() {
      if (!this.chartData?.categories || this.chartData.categories.length === 0) return []
      return this.chartData.categories.map((cat) => {
        if (cat && DateTime.fromFormat(cat, 'dd/MM/yyyy').isValid) {
          const formatted = DateTime.fromFormat(cat, 'dd/MM/yyyy').setLocale('pt-BR').toFormat('dd MMM')
          const parts = formatted.split(' ')
          if (parts.length === 2) {
            const day = parts[0]
            const month = parts[1].replace('.', '')
            const monthCapitalized = month.charAt(0).toUpperCase() + month.slice(1)
            return `${day} ${monthCapitalized}`
          }
          return formatted.replace('.', '')
        }
        return cat
      })
    },
    campaignStartIndex() {
      if (!this.campaign?.start_date || !this.chartData?.categories) return -1
      const startDate = DateTime.fromISO(this.campaign.start_date).toFormat('dd/MM/yyyy')
      return this.chartData.categories.findIndex((cat) => cat === startDate)
    },
    campaignEndIndex() {
      if (!this.campaign?.end_date || !this.chartData?.categories) return -1
      const endDate = DateTime.fromISO(this.campaign.end_date).toFormat('dd/MM/yyyy')
      return this.chartData.categories.findIndex((cat) => cat === endDate)
    },
    campaignVigentEndDate() {
      if (!this.campaign?.end_date) return null
      const dtEnd = DateTime.fromISO(this.campaign.end_date)
      const today = DateTime.now().startOf('day')
      return dtEnd < today ? dtEnd : today
    },
    campaignVigentEndIndex() {
      if (!this.campaignVigentEndDate || !this.chartData?.categories) return -1
      const endDateFormatted = this.campaignVigentEndDate.toFormat('dd/MM/yyyy')
      return this.chartData.categories.findIndex((cat) => cat === endDateFormatted)
    },
    campaignStartCategory() {
      if (this.campaignStartIndex < 0 || !this.xAxisCategories.length) return null
      if (this.chartViewMode === 'bar' && this.campaignStartIndex > 0) {
        return this.xAxisCategories[this.campaignStartIndex - 1]
      }
      return this.xAxisCategories[this.campaignStartIndex]
    },
    campaignEndCategory() {
      if (this.campaignVigentEndIndex < 0 || !this.xAxisCategories.length) return null
      if (this.chartViewMode === 'bar' && this.campaignVigentEndIndex < this.xAxisCategories.length - 1) {
        return this.xAxisCategories[this.campaignVigentEndIndex + 1]
      }
      return this.xAxisCategories[this.campaignVigentEndIndex]
    },
    currentOfferTypeName() {
      if (this.offerType) {
        return getOfferTypeName(this.offerType)
      }
      if (this.campaign?.types && this.campaign.types.length > 0) {
        return getOfferTypeName(this.campaign.types[0])
      }
      return 'general'
    },
    productOptions() {
      const offerTypeName = this.currentOfferTypeName
      const productMetrics = this.campaign?.daily_metrics?.data?.[offerTypeName]?.product_metrics || []
      if (!Array.isArray(productMetrics)) return []

      const options = productMetrics.map((pm) => {
        const product = Array.isArray(this.products)
          ? this.products.find((p) => p?.sku === pm?.id || p?.ean === pm?.id || (Array.isArray(p?.eans) && p?.eans.includes(pm?.id)))
          : null

        const name = product?.name ? ` - ${product.name}` : ''
        return { text: `${pm.id}${name}`.trim(), value: pm.id }
      })

      if (!options.length) return []

      return [{ text: 'Todos os produtos', value: 'ALL' }, ...options]
    },

    queryParamFranchise() {
      return typeof this.$route?.query?.franchise === 'string' ? this.$route?.query?.franchise === 'true' : this.$route?.query?.franchise
    },

    chartOptions() {
      return {
        chart: {
          type: this.chartViewMode,
          height: 200,
          defaultLocale: 'pt-br',
          originalCategories: this.chartData?.categories || [],
          locales: [
            {
              name: 'pt-br',
              options: {
                months: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
                shortMonths: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
                days: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
                shortDays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
              },
            },
          ],
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          selection: {
            enabled: false,
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 500,
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
          background: '#FFFFFF',
          foreColor: '#374151',
        },
        colors: ['#969EA7', '#00B395', '#002040'],
        stroke: {
          curve: 'smooth',
          width: this.chartViewMode === 'bar' ? 0 : 2,
        },
        fill: {
          type: 'solid',
          opacity: 1,
        },
        plotOptions:
          this.chartViewMode === 'bar'
            ? {
                bar: {
                  horizontal: false,
                  columnWidth: '55%',
                  borderRadius: 4,
                  dataLabels: {
                    position: 'top',
                  },
                },
              }
            : {},
        xaxis: {
          categories: this.xAxisCategories,
          labels: {
            style: {
              colors: '#6B7280',
              fontSize: '12px',
            },
            hideOverlappingLabels: false,
            showDuplicates: true,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          crosshairs: {
            show: false,
          },
        },
        yaxis: {
          min: 0,
          max: this.yAxisMax,
          tickAmount: Math.min(this.yAxisMax, 5),
          labels: {
            style: {
              colors: '#6B7280',
              fontSize: '12px',
            },
            formatter: (value) => Math.round(value),
          },
        },
        grid: {
          borderColor: '#E5E7EB',
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        legend: {
          show: false,
        },
        tooltip: {
          shared: this.chartViewMode === 'bar',
          intersect: false,
          theme: 'light',
          custom: ({ series, dataPointIndex, w }) => {
            try {
              const names = (w && w.config && w.config.series ? w.config.series.map((s) => s.name) : []) || []
              const colors = (w && w.config && w.config.colors) || []
              const values = (w && w.globals && w.globals.series ? w.globals.series.map((arr) => arr[dataPointIndex]) : []) || []
              const originalCategories = w?.config?.chart?.originalCategories || []
              let xLabel = ''
              if (originalCategories && originalCategories.length > dataPointIndex && originalCategories[dataPointIndex]) {
                xLabel = originalCategories[dataPointIndex]
              } else {
                const categories = w?.config?.xaxis?.categories || []
                xLabel = categories[dataPointIndex] || ''
              }

              const items = []
              let shouldHideSegment = false
              if (this.campaign?.start_date && xLabel) {
                try {
                  const pointDate = DateTime.fromFormat(xLabel, 'dd/MM/yyyy')
                  const campaignStart = DateTime.fromISO(this.campaign.start_date)
                  if (pointDate.isValid && campaignStart.isValid) {
                    shouldHideSegment = pointDate >= campaignStart
                  }
                } catch (e) {}
              }

              for (let i = 0; i < values.length; i++) {
                const val = values[i]
                if (val === null || val === undefined) continue
                if (shouldHideSegment && names[i] === 'Clientes do segmento') continue
                items.push({ name: names[i], color: colors[i], val })
              }
              if (!items.length) return ''

              const format = (v) => `${Math.floor(Number(v) * 10) / 10} produtos`

              const rows = items
                .map(
                  (it) =>
                    `<div style="display:flex;align-items:center;margin:6px 0;gap:8px;">
                      <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${it.color}"></span>
                      <span style="color:#6B7580;">${it.name}:</span>
                      <b style="color:#374151;">${format(it.val)}</b>
                    </div>`
                )
                .join('')

              return `<div style="padding:8px 10px;">` + `<div style="font-weight:600;margin-bottom:6px;color:#374151;">${xLabel}</div>` + rows + `</div>`
            } catch (e) {
              return ''
            }
          },
        },
        markers:
          this.chartViewMode === 'bar'
            ? { size: 0 }
            : {
                size: 3,
                strokeWidth: 1,
                hover: {
                  size: 5,
                },
              },
        dataLabels: {
          enabled: false,
        },
        annotations: {
          xaxis: [
            {
              x: this.campaignStartCategory,
              x2: this.campaignEndCategory,
              fillColor: '#D1FAE5',
              strokeDashArray: 0,
              borderColor: '#00B345',
              offsetX: this.chartViewMode === 'bar' ? 65 : 0,
              style: {
                color: '#fff',
                background: '#775DD0',
              },
              opacity: 0.3,
              label: {
                show: false,
              },
            },
          ],
          points: [
            {
              x: this.campaignStartIndex >= 0 ? this.campaignStartIndex : null,
              y: 5,
              marker: {
                size: 0,
                fillColor: '#10B981',
                strokeColor: '#10B981',
                strokeWidth: 2,
                radius: 2,
              },
            },
            {
              x: this.campaignVigentEndIndex >= 0 ? this.campaignVigentEndIndex : null,
              y: 5,
              marker: {
                size: 0,
                fillColor: '#10B981',
                strokeColor: '#10B981',
                strokeWidth: 2,
                radius: 2,
              },
            },
          ],
        },
      }
    },
  },
  watch: {
    campaign: {
      handler(newValue) {
        if (newValue && newValue._id) {
          this.$nextTick(() => {
            this.initializeChartData()
          })
        } else {
          this.selectedProduct = null
          this.chartData = { categories: [], series: [] }
          this.fullChartData = { categories: [], series: [] }
        }
      },
      immediate: true,
    },

    selectedProduct() {
      const hadData = this.fullChartData.categories.length > 0
      this.generateChartData()
      if (hadData) {
        this.$nextTick(() => {
          const interval = 14
          const totalCategories = this.fullChartData.categories.length
          if (totalCategories > interval) {
            this.currentPosition = totalCategories - interval
          } else {
            this.currentPosition = 0
          }
          this.applyPagination()
        })
      }
    },

    chartViewMode() {
      const hadData = this.fullChartData.categories.length > 0
      this.generateChartData()
      if (hadData) {
        this.$nextTick(() => {
          const interval = 14
          const totalCategories = this.fullChartData.categories.length
          if (totalCategories > interval) {
            this.currentPosition = totalCategories - interval
          } else {
            this.currentPosition = 0
          }
          this.applyPagination()
        })
      }
    },
  },

  mounted() {
    if (this.campaign && this.campaign._id) {
      this.initializeChartData()
    }
  },

  methods: {
    initializeChartData() {
      const options = this.productOptions || []
      if (!options.length) {
        this.selectedProduct = null
        this.chartData = { categories: [], series: [] }
        this.fullChartData = { categories: [], series: [] }
        return
      }
      if (!this.selectedProduct) {
        this.selectedProduct = options[0].value
      }
      this.generateChartData()
    },
    generateChartData() {
      const offerTypeName = this.currentOfferTypeName
      const productMetrics = this.campaign?.daily_metrics?.data?.[offerTypeName]?.product_metrics || []
      const dailyMetrics = this.campaign?.daily_metrics?.data?.[offerTypeName]?.daily_metrics || []

      const dtStart = DateTime.fromISO(this.campaign.start_date)
      const dtEnd = DateTime.fromISO(this.campaign.end_date)
      const campaignDurationDays = dtEnd.diff(dtStart, 'days').days + 1

      const previousDays = []
      for (let i = campaignDurationDays; i > 0; i--) {
        const day = dtStart.minus({ days: i })
        previousDays.push({
          id: day.toISODate(),
          dateFormatted: day.toFormat('dd/MM/yyyy'),
        })
      }

      const campaignDays = []
      const today = DateTime.now().startOf('day')
      const endDate = dtEnd < today ? dtEnd : today

      let currentDay = dtStart
      while (currentDay <= endDate) {
        campaignDays.push({
          id: currentDay.toISODate(),
          dateFormatted: currentDay.toFormat('dd/MM/yyyy'),
        })
        currentDay = currentDay.plus({ days: 1 })
      }

      switch (this.selectedProduct) {
        case 'ALL': {
          if (!Array.isArray(dailyMetrics) || dailyMetrics.length === 0) {
            const allCategories = [...previousDays.map((d) => d.dateFormatted), ...campaignDays.map((d) => d.dateFormatted)]
            const previousDaysCount = previousDays.length
            const previousDaysZeroData = new Array(previousDaysCount).fill(0)
            const previousDaysNullData = new Array(previousDaysCount).fill(null)
            const campaignZeroData = new Array(campaignDays.length).fill(0)
            const isLineChart = this.chartViewMode === 'line'

            const campaignSegmentData = isLineChart ? [0, ...new Array(campaignDays.length - 1).fill(null)] : new Array(campaignDays.length).fill(null)

            this.fullChartData.categories = allCategories
            this.fullChartData.series = [
              { name: 'Clientes do segmento', data: [...previousDaysZeroData, ...campaignSegmentData] },
              { name: 'Clientes que receberam a oferta', data: [...previousDaysNullData, ...campaignZeroData] },
              { name: 'Clientes que não receberam a oferta', data: [...previousDaysNullData, ...campaignZeroData] },
            ]
            this.applyPagination()
            return
          }

          const backendDataMap = new Map()
          dailyMetrics.forEach((dm) => {
            if (dm?.id) {
              const dateFormatted = DateTime.fromISO(dm.id).isValid ? DateTime.fromISO(dm.id).toFormat('dd/MM/yyyy') : dm.id
              backendDataMap.set(dateFormatted, dm)
            }
          })

          const allCategories = []
          const segmentData = []
          const offerData = []
          const noOfferData = []

          previousDays.forEach((prevDay) => {
            allCategories.push(prevDay.dateFormatted)
            const backendData = backendDataMap.get(prevDay.dateFormatted)
            if (backendData) {
              const segAvg = backendData?.average_product_quantity?.segments?.average_by_clients || 0
              segmentData.push(segAvg)
            } else {
              segmentData.push(0)
            }
            offerData.push(null)
            noOfferData.push(null)
          })

          campaignDays.forEach((campDay, index) => {
            allCategories.push(campDay.dateFormatted)
            const backendData = backendDataMap.get(campDay.dateFormatted)
            const isFirstDayOfCampaign = index === 0
            const isLineChart = this.chartViewMode === 'line'

            if (backendData) {
              const segAvg = backendData?.average_product_quantity?.segments?.average_by_clients ?? 0
              const salesAvg = backendData?.average_product_quantity?.sales?.average_by_clients ?? 0
              const wSalesAvg = backendData?.average_product_quantity?.without_sales?.average_by_clients ?? 0

              if (isLineChart && isFirstDayOfCampaign) {
                segmentData.push(segAvg)
              } else {
                segmentData.push(null)
              }
              offerData.push(salesAvg)
              noOfferData.push(wSalesAvg)
            } else {
              if (isLineChart && isFirstDayOfCampaign) {
                segmentData.push(0)
              } else {
                segmentData.push(null)
              }
              offerData.push(0)
              noOfferData.push(0)
            }
          })

          dailyMetrics.forEach((dm) => {
            const dateISO = dm?.id
            const current = DateTime.fromISO(dateISO)
            const dateFormatted = current.isValid ? current.toFormat('dd/MM/yyyy') : dateISO

            if (!previousDays.find((pd) => pd.dateFormatted === dateFormatted) && !campaignDays.find((cd) => cd.dateFormatted === dateFormatted)) {
              allCategories.push(dateFormatted)

              const salesAvg = dm?.average_product_quantity?.sales?.average_by_clients ?? 0
              const wSalesAvg = dm?.average_product_quantity?.without_sales?.average_by_clients ?? 0

              segmentData.push(null)
              offerData.push(salesAvg)
              noOfferData.push(wSalesAvg)
            }
          })

          const sortedData = allCategories
            .map((cat, index) => ({
              category: cat,
              segment: segmentData[index],
              offer: offerData[index],
              noOffer: noOfferData[index],
              date: DateTime.fromFormat(cat, 'dd/MM/yyyy').isValid ? DateTime.fromFormat(cat, 'dd/MM/yyyy').toMillis() : 0,
            }))
            .sort((a, b) => a.date - b.date)

          this.fullChartData.categories = sortedData.map((d) => d.category)
          this.fullChartData.series = [
            { name: 'Clientes do segmento', data: sortedData.map((d) => d.segment) },
            { name: 'Clientes que receberam a oferta', data: sortedData.map((d) => d.offer) },
            { name: 'Clientes que não receberam a oferta', data: sortedData.map((d) => d.noOffer) },
          ]
          break
        }
        default: {
          if (!this.selectedProduct) return
          if (!Array.isArray(productMetrics)) return

          const pm = productMetrics.find((p) => p?.id === this.selectedProduct)
          if (!pm || !Array.isArray(pm.daily_metrics)) {
            const allCategories = [...previousDays.map((d) => d.dateFormatted), ...campaignDays.map((d) => d.dateFormatted)]
            const previousDaysCount = previousDays.length
            const previousDaysZeroData = new Array(previousDaysCount).fill(0)
            const previousDaysNullData = new Array(previousDaysCount).fill(null)
            const campaignZeroData = new Array(campaignDays.length).fill(0)
            const isLineChart = this.chartViewMode === 'line'

            const campaignSegmentData = isLineChart ? [0, ...new Array(campaignDays.length - 1).fill(null)] : new Array(campaignDays.length).fill(null)

            this.fullChartData.categories = allCategories
            this.fullChartData.series = [
              { name: 'Clientes do segmento', data: [...previousDaysZeroData, ...campaignSegmentData] },
              { name: 'Clientes que receberam a oferta', data: [...previousDaysNullData, ...campaignZeroData] },
              { name: 'Clientes que não receberam a oferta', data: [...previousDaysNullData, ...campaignZeroData] },
            ]
            this.applyPagination()
            return
          }

          const backendDataMap = new Map()
          pm.daily_metrics.forEach((dm) => {
            if (dm?.id) {
              const dateFormatted = DateTime.fromISO(dm.id).isValid ? DateTime.fromISO(dm.id).toFormat('dd/MM/yyyy') : dm.id
              backendDataMap.set(dateFormatted, dm)
            }
          })

          const allCategories = []
          const segmentData = []
          const offerData = []
          const noOfferData = []

          previousDays.forEach((prevDay) => {
            allCategories.push(prevDay.dateFormatted)
            const backendData = backendDataMap.get(prevDay.dateFormatted)
            if (backendData) {
              const segAvg = backendData?.average_product_quantity?.segments?.average_by_clients || 0
              segmentData.push(segAvg)
            } else {
              segmentData.push(0)
            }
            offerData.push(null)
            noOfferData.push(null)
          })

          campaignDays.forEach((campDay, index) => {
            allCategories.push(campDay.dateFormatted)
            const backendData = backendDataMap.get(campDay.dateFormatted)
            const isFirstDayOfCampaign = index === 0
            const isLineChart = this.chartViewMode === 'line'

            if (backendData) {
              const segAvg = backendData?.average_product_quantity?.segments?.average_by_clients ?? 0
              const salesAvg = backendData?.average_product_quantity?.sales?.average_by_clients ?? 0
              const wSalesAvg = backendData?.average_product_quantity?.without_sales?.average_by_clients ?? 0

              if (isLineChart && isFirstDayOfCampaign) {
                segmentData.push(segAvg)
              } else {
                segmentData.push(null)
              }
              offerData.push(salesAvg)
              noOfferData.push(wSalesAvg)
            } else {
              if (isLineChart && isFirstDayOfCampaign) {
                segmentData.push(0)
              } else {
                segmentData.push(null)
              }
              offerData.push(0)
              noOfferData.push(0)
            }
          })

          pm.daily_metrics.forEach((d) => {
            const dateISO = d?.id
            const current = DateTime.fromISO(dateISO)
            const dateFormatted = current.isValid ? current.toFormat('dd/MM/yyyy') : dateISO

            if (!previousDays.find((pd) => pd.dateFormatted === dateFormatted) && !campaignDays.find((cd) => cd.dateFormatted === dateFormatted)) {
              allCategories.push(dateFormatted)

              const segAvg = d?.average_product_quantity?.segments?.average_by_clients || 0
              const salesAvg = d?.average_product_quantity?.sales?.average_by_clients || 0
              const wSalesAvg = d?.average_product_quantity?.without_sales?.average_by_clients || 0

              segmentData.push(segAvg)
              offerData.push(salesAvg || 0)
              noOfferData.push(wSalesAvg || 0)
            }
          })

          const sortedData = allCategories
            .map((cat, index) => ({
              category: cat,
              segment: segmentData[index],
              offer: offerData[index],
              noOffer: noOfferData[index],
              date: DateTime.fromFormat(cat, 'dd/MM/yyyy').isValid ? DateTime.fromFormat(cat, 'dd/MM/yyyy').toMillis() : 0,
            }))
            .sort((a, b) => a.date - b.date)

          this.fullChartData.categories = sortedData.map((d) => d.category)
          this.fullChartData.series = [
            { name: 'Clientes do segmento', data: sortedData.map((d) => d.segment) },
            { name: 'Clientes que receberam a oferta', data: sortedData.map((d) => d.offer) },
            { name: 'Clientes que não receberam a oferta', data: sortedData.map((d) => d.noOffer) },
          ]
          break
        }
      }

      this.currentPosition = 0
      this.applyPagination()
    },

    updateCurrentPosition(position) {
      this.currentPosition = position
      this.applyPagination()
    },

    applyPagination() {
      const interval = 14
      const startIndex = this.currentPosition
      const endIndex = Math.min(startIndex + interval, this.fullChartData.categories.length)

      this.chartData.categories = [...this.fullChartData.categories.slice(startIndex, endIndex)]

      this.chartData.series = this.fullChartData.series.map((series) => {
        const points = []
        for (let i = startIndex; i < endIndex; i++) {
          const y = series.data[i]
          points.push(y)
        }
        return { ...series, data: [...points] }
      })
    },

    getDataToExport() {
      const offerTypeName = this.currentOfferTypeName
      const productMetrics = this.campaign?.daily_metrics?.data?.[offerTypeName]?.product_metrics || []

      const toProductLabel = (id) => {
        if (Array.isArray(this.products)) {
          const p = this.products.find((prod) => prod?.sku === id || prod?.ean === id || (Array.isArray(prod?.eans) && prod.eans.includes(id)))

          if (p?.name) {
            return p.name
          }
        }

        return id
      }

      const productsToExport = this.selectedProduct === 'ALL' ? productMetrics : productMetrics.filter((p) => p?.id === this.selectedProduct)

      const dateSet = new Set()
      productsToExport.forEach((pm) => {
        const daily = Array.isArray(pm?.daily_metrics) ? pm.daily_metrics : []
        daily.forEach((dm) => {
          if (dm?.id) dateSet.add(dm.id)
        })
      })

      const isoDates = Array.from(dateSet).sort((a, b) => (a > b ? 1 : a < b ? -1 : 0))
      const dateLabels = isoDates.map((iso) => (DateTime.fromISO(iso).isValid ? DateTime.fromISO(iso).toFormat('dd/MM/yyyy') : iso))

      const headers = ['Produto', 'Grupo', ...dateLabels]

      const dtStart = DateTime.fromISO(this.campaign.start_date)

      const avgValue = (src) => {
        if (typeof src?.average_by_clients === 'number') {
          return src.average_by_clients
        }
        const qty = Number(src?.product_quantity || 0)
        const clients = Number(src?.clients_count || 0)
        if (!clients) return 0
        return qty / clients
      }

      const groups = [
        { key: 'segments', label: 'Clientes do segmento' },
        { key: 'sales', label: 'Clientes que receberam a oferta' },
        {
          key: 'without_sales',
          label: 'Clientes que não receberam a oferta',
        },
      ]

      const rows = []

      productsToExport.forEach((pm) => {
        const byDate = {}
        const daily = Array.isArray(pm?.daily_metrics) ? pm.daily_metrics : []
        daily.forEach((dm) => {
          if (dm?.id) byDate[dm.id] = dm
        })

        groups.forEach((g) => {
          const row = [toProductLabel(pm.id), g.label]

          isoDates.forEach((iso) => {
            const current = DateTime.fromISO(iso)
            const beforeOrAtStart = current <= dtStart
            const atOrAfterStart = current >= dtStart

            const dm = byDate[iso]
            const src = dm?.average_product_quantity?.[g.key]

            let shouldShow = false
            if (g.key === 'segments') {
              shouldShow = beforeOrAtStart
            } else {
              shouldShow = atOrAfterStart
            }

            if (shouldShow && src) {
              const val = Math.round(avgValue(src) * 10) / 10
              row.push(val)
            } else {
              row.push('')
            }
          })

          rows.push(row)
        })
      })

      return { headers, rows }
    },
  },
}
</script>

<style lang="scss" scoped>
.campaign-impact-chart-card {
  ::v-deep .v-card__title {
    padding-top: 24px;
    padding-bottom: 16px;
  }

  ::v-deep .v-card__text {
    padding-bottom: 24px;
  }

  .chart-title {
    font-size: 18px;
    font-weight: 700;
    color: #4b525a;
    line-height: 120%;
    margin: 0;
  }

  .chart-description {
    font-size: 16px;
    font-weight: 400;
    color: #6b7580;
    line-height: 1.2;
    letter-spacing: 0.4px;
    max-width: 50vw;
  }

  .chart-view-toggle {
    .v-btn {
      min-width: 40px;
      padding: 0 12px;
    }
  }

  .chart-container {
    position: relative;
    min-height: 200px;
    margin-bottom: 24px;
  }

  .chart-legend {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;

    .legend-section {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #6b7280;

        .legend-color {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;

          &.segment-clients {
            background-color: #969ea7;
          }

          &.offer-clients {
            background-color: #00b395;
          }

          &.no-offer-clients {
            background-color: #002040;
          }
        }

        .legend-period {
          width: 16px;
          height: 16px;
          background-color: #d1fae5;
          border: 1px solid #10b981;
          flex-shrink: 0;
        }
      }
    }

    .chart-navigator {
      flex-shrink: 0;
    }
  }
}

@media (max-width: 768px) {
  .campaign-impact-chart-card {
    .chart-legend {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      .legend-section {
        flex-direction: column;
        gap: 12px;
      }
    }
  }
}
</style>
